import { Badge, Button, Dialog, Input, Select } from '@artosera/ui';
import type { BadgeTone } from '@artosera/ui';
import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { urlRappelSso } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { estErreurStepUpRequis } from '../../api/coffre.js';
import {
  useConnecteurs,
  useEcrireConnecteur,
  useRetirerConnecteur,
  useVerifierConnecteur,
  type ChampsConnecteur,
  type VueConnecteur,
} from '../../api/connecteurs.js';
import { moduleDeLaRoute } from '@artosera/shared';
import type { GalerieSession } from '../../api/types.js';
import { galerieALaction, moduleEnLectureSeule } from '../../auth/sujet.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { Field } from '../Field.js';
import { useShellContext } from '../shell/context.js';
import styles from './ConnecteursPage.module.css';

function ton(vue: VueConnecteur): BadgeTone {
  if (vue.erreur !== null) return 'withdrawn';
  if (!vue.configure) return 'neutral';
  return vue.verifieLe === null ? 'reserved' : 'available';
}

function AdressesSso({ galerie }: { galerie: string }): JSX.Element {
  const { t } = useTranslation('regie');
  const [message, setMessage] = useState<{ ok: boolean; texte: string } | null>(null);
  const adresses = [
    { cle: 'redirection', valeur: urlRappelSso() },
    {
      cle: 'lien',
      valeur: `${window.location.origin}/connexion/sso?galerie=${encodeURIComponent(galerie)}`,
    },
  ];
  async function copier(valeur: string) {
    try {
      await navigator.clipboard.writeText(valeur);
      setMessage({ ok: true, texte: t('connecteurs.oidc.copie') });
    } catch {
      setMessage({ ok: false, texte: t('connecteurs.oidc.copieImpossible') });
    }
  }
  return (
    <div>
      <dl className={styles.adressesSso}>
        {adresses.map(({ cle, valeur }) => (
          <div key={cle}>
            <dt id={`sso-adresse-${cle}`}>{t(`connecteurs.oidc.${cle}`)}</dt>
            <dd>
              <code>{valeur}</code>
              <Button
                type="button"
                variant="secondary"
                className={styles.copierSso}
                aria-describedby={`sso-adresse-${cle}`}
                onClick={() => void copier(valeur)}
              >
                {t('connecteurs.oidc.copier')}
              </Button>
            </dd>
          </div>
        ))}
      </dl>
      {message && (
        <p
          className={message.ok ? styles.copieSso : styles.erreur}
          role={message.ok ? 'status' : 'alert'}
        >
          {message.texte}
        </p>
      )}
    </div>
  );
}

function CarteConnecteur({
  galerie,
  vue,
  champs,
  simulation,
  modifiable,
}: {
  galerie: string;
  vue: VueConnecteur;
  champs: ChampsConnecteur;
  simulation: boolean;
  modifiable: boolean;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const ecrire = useEcrireConnecteur(galerie);
  const retirer = useRetirerConnecteur(galerie);
  const verifier = useVerifierConnecteur(galerie);
  const [mode, setMode] = useState(vue.mode);
  const [parametres, setParametres] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      champs.parametres.map((champ) => [
        champ.cle,
        vue.parametres[champ.cle] ?? (champ.type === 'choix' ? (champ.choix?.[0] ?? '') : ''),
      ]),
    ),
  );
  const [remplacer, setRemplacer] = useState(vue.finSecret === null);
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [elevation, setElevation] = useState<null | (() => void)>(null);
  const [confirmerRetrait, setConfirmerRetrait] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const titreId = `connecteur-${vue.id}`;

  const geste = (executer: () => Promise<unknown>) => {
    setMessage(null);
    executer()
      .catch((erreur: unknown) => {
        if (estErreurStepUpRequis(erreur)) setElevation(() => () => geste(executer));
        else setMessage(estApiError(erreur) ? erreur.message : t('connecteurs.erreurReseau'));
      })
      .finally(() => {
        ecrire.reset();
        retirer.reset();
        verifier.reset();
      });
  };

  const enregistrer = (evenement: FormEvent) => {
    evenement.preventDefault();
    const avecSecrets = remplacer && Object.values(secrets).some((valeur) => valeur.length > 0);
    geste(() =>
      ecrire
        .mutateAsync({
          id: vue.id,
          corps: { revision: vue.revision, mode, parametres, ...(avecSecrets ? { secrets } : {}) },
        })
        .then(() => {
          setSecrets({});
          setRemplacer(false);
        }),
    );
  };

  return (
    <section className={styles.carte} aria-labelledby={titreId}>
      <header className={styles.entete}>
        <h2 id={titreId} className={styles.titreCarte}>
          {t(`connecteurs.noms.${vue.id}`)}
        </h2>
        <Badge tone={ton(vue)}>
          {vue.erreur !== null
            ? t('connecteurs.etats.erreur', { code: t(`connecteurs.erreurs.${vue.erreur}`) })
            : !vue.configure
              ? t('connecteurs.etats.nonConfigure')
              : vue.verifieLe === null
                ? t('connecteurs.etats.nonVerifie')
                : t('connecteurs.etats.verifie')}
        </Badge>
      </header>
      {vue.id === 'oidc' && <AdressesSso galerie={galerie} />}
      <form className={styles.formulaire} onSubmit={enregistrer} noValidate>
        {simulation ? (
          <Field label={t('connecteurs.mode.libelle')} htmlFor={`${titreId}-mode`}>
            <Select
              id={`${titreId}-mode`}
              value={mode}
              disabled={!modifiable}
              onValueChange={(valeur) => setMode(valeur === 'simule' ? 'simule' : 'reel')}
              options={[
                { value: 'simule', label: t('connecteurs.mode.simule') },
                { value: 'reel', label: t('connecteurs.mode.reel') },
              ]}
            />
          </Field>
        ) : null}
        {champs.parametres.map((champ) => (
          <Field
            key={champ.cle}
            label={t(`connecteurs.champs.${vue.id}.${champ.cle}`)}
            htmlFor={`${titreId}-${champ.cle}`}
          >
            {champ.type === 'choix' ? (
              <Select
                id={`${titreId}-${champ.cle}`}
                value={parametres[champ.cle] ?? ''}
                disabled={!modifiable}
                onValueChange={(valeur) => setParametres({ ...parametres, [champ.cle]: valeur })}
                options={(champ.choix ?? []).map((choix) => ({
                  value: choix,
                  label: t(`connecteurs.choix.${choix}`, { defaultValue: choix }),
                }))}
              />
            ) : (
              <Input
                id={`${titreId}-${champ.cle}`}
                type={champ.type === 'email' ? 'email' : champ.type === 'url' ? 'url' : 'text'}
                value={parametres[champ.cle] ?? ''}
                required={champ.requis}
                readOnly={!modifiable}
                onChange={(e) => setParametres({ ...parametres, [champ.cle]: e.target.value })}
              />
            )}
          </Field>
        ))}
        {vue.finSecret !== null ? <p className={styles.secret}>{`•••• ${vue.finSecret}`}</p> : null}
        {modifiable && vue.finSecret !== null ? (
          <label className={styles.remplacer}>
            <input
              type="checkbox"
              checked={remplacer}
              onChange={(e) => {
                setRemplacer(e.target.checked);
                if (!e.target.checked) setSecrets({});
              }}
            />
            {t('connecteurs.actions.remplacerSecret')}
          </label>
        ) : null}
        {modifiable && remplacer
          ? champs.secrets.map((champ) => (
              <Field
                key={champ.cle}
                label={t(`connecteurs.champs.${vue.id}.${champ.cle}`)}
                htmlFor={`${titreId}-${champ.cle}`}
              >
                <Input
                  id={`${titreId}-${champ.cle}`}
                  type="password"
                  autoComplete="off"
                  value={secrets[champ.cle] ?? ''}
                  onChange={(e) => setSecrets({ ...secrets, [champ.cle]: e.target.value })}
                />
              </Field>
            ))
          : null}
        {message !== null ? (
          <p role="alert" className={styles.erreur}>
            {message}
          </p>
        ) : null}
        {modifiable ? (
          <div className={styles.actions}>
            <Button
              type="submit"
              loading={ecrire.isPending}
              disabled={verifier.isPending || retirer.isPending}
            >
              {t('connecteurs.actions.enregistrer')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!vue.configure || ecrire.isPending || retirer.isPending}
              loading={verifier.isPending}
              onClick={() => geste(() => verifier.mutateAsync(vue.id))}
            >
              {t('connecteurs.actions.verifier')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={
                vue.revision === 0 || ecrire.isPending || verifier.isPending || retirer.isPending
              }
              onClick={() => setConfirmerRetrait(true)}
            >
              {t('connecteurs.actions.retirer')}
            </Button>
          </div>
        ) : null}
      </form>
      <Dialog
        open={confirmerRetrait}
        onOpenChange={setConfirmerRetrait}
        title={t('connecteurs.retrait.titre')}
        description={t('connecteurs.retrait.texte')}
        closeLabel={t('connecteurs.fermer')}
        footer={
          <Button
            variant="danger"
            onClick={() => {
              setConfirmerRetrait(false);
              geste(() => retirer.mutateAsync(vue.id));
            }}
          >
            {t('connecteurs.actions.retirer')}
          </Button>
        }
      />
      <StepUpDialog
        open={elevation !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) {
            setElevation(null);
            setSecrets({});
          }
        }}
        onVerifie={() => {
          const rejouer = elevation;
          setElevation(null);
          rejouer?.();
        }}
        description={t('connecteurs.elevation')}
      />
    </section>
  );
}

/** Le connecteur appartient-il à un module que la galerie ne consulte qu'en lecture seule ? */
function connecteurEnLectureSeule(galerie: GalerieSession, id: string): boolean {
  const module = moduleDeLaRoute(`/connecteurs/${id}`);
  return module !== null && moduleEnLectureSeule(galerie, module);
}

/** Réglages › Connecteurs (M0) : un connecteur par module actif qui en consomme un. */
export function ConnecteursPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const requete = useConnecteurs(galerieSlug);
  const modifiable = galerieALaction(galerie, 'settings:write');
  return (
    <div className={styles.page} data-ui="connectors">
      <h1 className={styles.titre} data-ui="page-heading">
        {t('connecteurs.titre')}
      </h1>
      <p className={styles.sousTitre}>{t('connecteurs.sousTitre')}</p>
      {requete.isLoading ? (
        <div className={styles.chargement} aria-busy="true" role="status">
          <p>{t('connecteurs.chargement')}</p>
          <div className={styles.squelette} aria-hidden="true" />
          <div className={styles.squelette} aria-hidden="true" />
        </div>
      ) : null}
      {requete.isError ? (
        <div role="alert">
          <p>{t('connecteurs.erreurLecture')}</p>
          <Button variant="secondary" onClick={() => void requete.refetch()}>
            {t('connecteurs.reessayer')}
          </Button>
        </div>
      ) : null}
      {requete.data && requete.data.connecteurs.length === 0 ? (
        <p className={styles.vide}>{t('connecteurs.vide')}</p>
      ) : null}
      {requete.data?.connecteurs.map((vue) => {
        const champs = requete.data.champs[vue.id];
        return champs ? (
          <CarteConnecteur
            key={`${galerieSlug}-${vue.id}-${String(vue.revision)}`}
            galerie={galerieSlug}
            vue={vue}
            champs={champs}
            simulation={requete.data.simulation}
            // Un connecteur dont le module est en lecture seule se lit, sans s'écrire.
            modifiable={modifiable && !connecteurEnLectureSeule(galerie, vue.id)}
          />
        ) : null;
      })}
    </div>
  );
}
